import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import styles from './ModuleNav.module.css';
import accents from '../moduleCard/accents.module.css';

// Distância (px) do topo da janela a partir da qual uma seção conta como "atual":
// cabeçalho fixo da aplicação mais uma folga.
const ACTIVE_OFFSET = 110;

// Altura do topo fixo da aplicação (faixas + cabeçalho, Header.css); 0 quando não é fixo
const topBarsHeight = () => parseFloat(
  window.getComputedStyle(document.documentElement).getPropertyValue('--app-top-bars-height'),
) || 0;

// Rolagem até a âncora: curta e com desaceleração suave (ease-out), em vez do
// `scrollIntoView` nativo, cuja aceleração é brusca.
const SCROLL_DURATION_MS = 450;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

function smoothScrollTo(element) {
  const marginTop = parseFloat(window.getComputedStyle(element).scrollMarginTop) || 0;
  const start = window.scrollY;
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  const target = Math.min(Math.max(element.getBoundingClientRect().top + start - marginTop, 0), maxScroll);
  const distance = target - start;
  if (Math.abs(distance) < 1) return;

  let startTime = null;
  let frame = null;
  const cancel = () => {
    if (frame !== null) window.cancelAnimationFrame(frame);
    window.removeEventListener('wheel', cancel);
    window.removeEventListener('touchstart', cancel);
  };

  const step = (now) => {
    if (startTime === null) startTime = now;
    const progress = Math.min((now - startTime) / SCROLL_DURATION_MS, 1);
    window.scrollTo(0, start + distance * easeOutCubic(progress));
    if (progress < 1) {
      frame = window.requestAnimationFrame(step);
    } else {
      cancel();
    }
  };

  // Se a pessoa rolar no meio do caminho, a animação para e não briga com ela
  window.addEventListener('wheel', cancel, { passive: true });
  window.addEventListener('touchstart', cancel, { passive: true });
  frame = window.requestAnimationFrame(step);
}

// Mantém o item ativo visível dentro do menu (que rola por dentro quando há
// muitos módulos), sem mexer na rolagem da página.
function keepInView(container, item) {
  if (!container || !item) return;
  const box = container.getBoundingClientRect();
  const rect = item.getBoundingClientRect();
  if (rect.top < box.top) container.scrollTop -= box.top - rect.top + 8;
  else if (rect.bottom > box.bottom) container.scrollTop += rect.bottom - box.bottom + 8;
  if (rect.left < box.left) container.scrollLeft -= box.left - rect.left + 8;
  else if (rect.right > box.right) container.scrollLeft += rect.right - box.right + 8;
}

/**
 * Menu lateral dos módulos: um atalho por card. Em telas estreitas vira uma
 * faixa horizontal fixa no topo.
 *
 * Dois modos:
 * - rolagem (padrão, tela de atendimento): o clique rola a página até a
 *   âncora `#id` e o item ativo acompanha a rolagem;
 * - controlado (ficha do paciente): com `onSelect`, o clique só avisa a
 *   página, que mostra a seção escolhida; `activeId` diz qual está aberta.
 *
 * `sections`: [{ id, label, icon, accent, locked?, lockedReason? }], na ordem
 * em que aparecem. Item `locked` continua visível (cadeado e tooltip com o
 * motivo), mas o clique não faz nada.
 * `title`/`ariaLabel`: rótulos do menu (padrão: módulos do atendimento).
 * `activeOffset`: distância do topo em que a seção vira a atual (a página
 * aumenta quando tem outra barra fixa acima dos cards).
 */
const ModuleNav = ({
  sections,
  title = 'Módulos',
  ariaLabel = 'Módulos do atendimento',
  activeOffset = ACTIVE_OFFSET,
  activeId: controlledId,
  onSelect,
}) => {
  const controlled = typeof onSelect === 'function';
  const [scrollActiveId, setScrollActiveId] = useState(sections[0]?.id || null);
  const activeId = controlled ? controlledId : scrollActiveId;
  // Os cards são recriados a cada render da página (cronômetro); o efeito só
  // precisa reagir quando a lista de âncoras muda de fato.
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const sectionIds = sections.map((section) => section.id).join('|');
  const navRef = useRef(null);
  const offsetRef = useRef(activeOffset);
  offsetRef.current = activeOffset;

  useEffect(() => {
    const nav = navRef.current;
    if (!nav || !activeId) return;
    keepInView(nav, nav.querySelector(`[data-section="${activeId}"]`));
  }, [activeId]);

  useEffect(() => {
    if (controlled) return undefined;
    let frame = null;

    const update = () => {
      frame = null;
      const list = sectionsRef.current;
      const offset = offsetRef.current + topBarsHeight();
      let current = list[0]?.id || null;
      list.forEach((section) => {
        const element = document.getElementById(section.id);
        if (element && element.getBoundingClientRect().top <= offset) {
          current = section.id;
        }
      });
      setScrollActiveId(current);
    };

    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [sectionIds, controlled]);

  function goTo(section) {
    if (section.locked) return;
    if (controlled) {
      onSelect(section.id);
      return;
    }
    const element = document.getElementById(section.id);
    if (!element) return;
    smoothScrollTo(element);
    setScrollActiveId(section.id);
  }

  function renderItem(section) {
    const isActive = section.id === activeId;
    const classes = [
      styles.item,
      accents[section.accent] || accents.blue,
      isActive ? styles.itemActive : '',
      section.locked ? styles.itemLocked : '',
    ].join(' ');

    const button = (
      <button
        key={section.id}
        type="button"
        data-section={section.id}
        className={classes}
        aria-current={isActive ? 'true' : undefined}
        aria-disabled={section.locked ? 'true' : undefined}
        onClick={() => goTo(section)}
      >
        <span className={styles.icon}>{section.icon}</span>
        <span className={styles.label}>{section.label}</span>
        {section.locked && <LockOutlinedIcon className={styles.lock} />}
      </button>
    );

    if (!section.locked || !section.lockedReason) return button;

    return (
      <Tooltip key={section.id} title={section.lockedReason} arrow placement="right">
        {button}
      </Tooltip>
    );
  }

  return (
    <nav ref={navRef} className={styles.nav} aria-label={ariaLabel}>
      <span className={styles.title}>{title}</span>
      {sections.map(renderItem)}
    </nav>
  );
};

export default ModuleNav;
